import { useEffect, useState } from "react";
import { websiteDesigns } from "../Ownerfiles/website-designs";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, LayoutTemplate, Monitor, Smartphone, Tablet, Check, Edit, Save, Palette, X } from "lucide-react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import { useToast } from "@/components/ui/use-toast";
import SchoolWebsiteEditor from "./SchoolWebsiteEditor";
import { createPortal } from "react-dom";
import { ThemeEditor } from "./ThemeEditor";
import { 
    SchoolTheme, 
    SectionVisibility, 
    NavItem,
    DEFAULT_THEME, 
    DEFAULT_SECTION_VISIBILITY,
    DEFAULT_NAV 
} from "../Ownerfiles/website-designs/types";

/**
 * SchoolWebsitePreview
 * 
 * Displays a preview of the school website with a tool to toggle between different designs.
 * Now includes an Editor toggle.
 */

// Helper component to render content inside an iframe with parent styles
const IFramePreview = ({ children }: { children: React.ReactNode }) => {
    const [contentRef, setContentRef] = useState<HTMLIFrameElement | null>(null);
    const [isReady, setIsReady] = useState(false);
    const mountNode = contentRef?.contentWindow?.document?.body;
    const head = contentRef?.contentWindow?.document?.head;

    useEffect(() => {
        if (head && !isReady) {
            // Copy all style and link tags from the parent to the iframe
            const styles = document.querySelectorAll('style, link[rel="stylesheet"]');
            styles.forEach(style => {
                head.appendChild(style.cloneNode(true));
            });
            
            // Add base tag to prevent navigation
            const baseTag = document.createElement('base');
            baseTag.target = '_self'; // Keep navigation within iframe
            head.appendChild(baseTag);
            
            // Add a small script to ensure clicks outside or internal links work safely
            const baseStyle = document.createElement('style');
            baseStyle.textContent = `
                body { margin: 0; padding: 0; background-color: white; overflow-x: hidden; width: 100%; height: auto; min-height: 100%; position: relative; font-family: sans-serif; }
                * { box-sizing: border-box; }
                /* Hide scrollbars for a cleaner preview */
                ::-webkit-scrollbar { display: none; }
                -ms-overflow-style: none;
                scrollbar-width: none;
            `;
            head.appendChild(baseStyle);
            setIsReady(true);
        }
    }, [head, isReady]);
    
    // Intercept clicks on anchor links to prevent parent navigation
    useEffect(() => {
        const iframeDoc = contentRef?.contentWindow?.document;
        if (!iframeDoc || !isReady) return;
        
        const handleClick = (e: MouseEvent) => {
            // Skip if already handled by component
            if (e.defaultPrevented) return;
            
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');
            if (anchor) {
                const href = anchor.getAttribute('href');
                // Allow external links to open in new tab
                if (href && href.startsWith('http')) {
                    return; // Let it proceed naturally (should open in new tab due to target="_blank")
                }
                // For hash links, handle scrolling internally
                if (href && href.startsWith('#')) {
                    e.preventDefault();
                    // Also close mobile menu if open
                    const mobileMenu = iframeDoc.getElementById('mobile-menu');
                    if (mobileMenu) {
                        mobileMenu.classList.add('hidden');
                    }
                    // Scroll after a small delay to let menu close
                    setTimeout(() => {
                        const targetId = href.replace('#', '');
                        const targetElement = iframeDoc.getElementById(targetId);
                        if (targetElement) {
                            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 100);
                }
            }
        };
        
        iframeDoc.addEventListener('click', handleClick);
        return () => iframeDoc.removeEventListener('click', handleClick);
    }, [contentRef, isReady]);

    return (
        <iframe 
            ref={setContentRef}
            className="w-full h-full border-none m-0 p-0"
            title="Website Preview"
        >
            {mountNode && isReady && createPortal(children, mountNode)}
        </iframe>
    );
};

const SchoolWebsitePreview = () => {
    const [previewData, setPreviewData] = useState<any>(null);
    const [selectedDesignIndex, setSelectedDesignIndex] = useState(0);
    const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
    const [isEditing, setIsEditing] = useState(false);
    const [showThemeEditor, setShowThemeEditor] = useState(false);
    const compatibleDesigns = websiteDesigns.filter((design) => design.id === 'modern-elite');
    const availableDesigns = compatibleDesigns.length > 0 ? compatibleDesigns : websiteDesigns.slice(0, 1);
    
    // Theme states
    const [theme, setTheme] = useState<SchoolTheme>(DEFAULT_THEME);
    const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>(DEFAULT_SECTION_VISIBILITY);
    const [navigation, setNavigation] = useState<NavItem[]>(DEFAULT_NAV);

    useEffect(() => {
        // Load preview data (simulating fetching from localStorage or DB)
        const storedData = localStorage.getItem('previewSchoolData'); 
        if (storedData) {
            const parsed = JSON.parse(storedData);
            setPreviewData(parsed);
            
            // Initialize theme from stored data
            if (parsed.theme) {
                setTheme({ ...DEFAULT_THEME, ...parsed.theme });
            } else if (parsed.colors) {
                // Legacy compatibility - use colors field
                setTheme(prev => ({
                    ...prev,
                    colors: { ...prev.colors, ...parsed.colors }
                }));
            }
            
            // Initialize section visibility
            if (parsed.sectionVisibility) {
                setSectionVisibility({ ...DEFAULT_SECTION_VISIBILITY, ...parsed.sectionVisibility });
            }
            
            // Initialize navigation
            if (parsed.nav && Array.isArray(parsed.nav)) {
                setNavigation(parsed.nav);
            }
        } else {
            // Fallback demo data if nothing stored
            setPreviewData({
                name: "Green Valley International",
                heroImages: ["https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop"],
            });
        }
    }, []);

    const currentDesign = availableDesigns[selectedDesignIndex] || availableDesigns[0];
    const DesignComponent = currentDesign.component;

    const nextDesign = () => {
        setSelectedDesignIndex((prev) => (prev + 1) % availableDesigns.length);
    };

    const prevDesign = () => {
        setSelectedDesignIndex((prev) => (prev - 1 + availableDesigns.length) % availableDesigns.length);
    };

    const { toast } = useToast();
    const [applying, setApplying] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSaveEdits = async () => {
        if (!previewData || !previewData.subdomain) {
            toast({ title: "Error", description: "No school selected to save to.", variant: "destructive" });
            return;
        }
        setSaving(true);
        try {
            const cleanSubdomain = previewData.subdomain.trim();
            console.log(`[Save] Saving edits to 'schoolWebsites/${cleanSubdomain}'`);
            
            const dataToSave = { 
                ...previewData, 
                lastUpdated: new Date().toISOString()
            };
            
            const schoolRef = doc(db, "schoolWebsites", cleanSubdomain);
            // Save all fields (including nested arrays)
            await setDoc(schoolRef, dataToSave, { merge: true });

            // Sync to localStorage so preview stays consistent
            localStorage.setItem('previewSchoolData', JSON.stringify(dataToSave));
            console.log('[Save] Data saved to Firestore and synced to localStorage');

            toast({ title: "Edits Saved", description: "Your changes have been saved and will appear on the live site." });
        } catch (error) {
            console.error(error);
            toast({ title: "Error", description: "Failed to save changes.", variant: "destructive" });
        } finally {
            setSaving(false);
        }
    };

    // Helper to update nested fields in previewData
    const handleFieldUpdate = (field: string, value: any) => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            
            // Deep clone
            const newData = JSON.parse(JSON.stringify(prev));
            
            // Handle array notation e.g. "team[0].img" or dot notation "hero.title"
            const parts = field.split(/\.|\[|\]/).filter(Boolean);
            
            let current = newData;
            for (let i = 0; i < parts.length - 1; i++) {
                const key = parts[i];
                if (!current[key]) current[key] = {};
                current = current[key];
            }
            
            const lastKey = parts[parts.length - 1];
            current[lastKey] = value;
            
            return newData;
        });
    };

    const handleApplyStyle = async () => {
        if (!previewData || !previewData.subdomain) {
            toast({ title: "Error", description: "Could not identify school to apply design to.", variant: "destructive" });
            return;
        }
        
        setApplying(true);
        try {
            const currentDesignId = currentDesign.id;
            
            // Write to schoolWebsites collection
            const cleanSubdomain = previewData.subdomain.trim();
            console.log(`[Publish] Writing design '${currentDesignId}' to 'schoolWebsites/${cleanSubdomain}'`);
            
            const schoolRef = doc(db, "schoolWebsites", cleanSubdomain);
            await setDoc(schoolRef, { 
                ...previewData, // Spread first so we don't overwrite new values
                selectedDesignId: currentDesignId, // Set this AFTER spreading
                lastUpdated: new Date().toISOString(),
                renderedHtml: null 
            }, { merge: true });

            toast({ 
                title: "Design Published!", 
                description: `This design is now live at ${cleanSubdomain}.sonoma.school`
            });
        } catch (error) {
            console.error(error);
            toast({ title: "Error", description: "Failed to apply design.", variant: "destructive" });
        } finally {
            setApplying(false);
        }
    };

    const handleClose = () => {
        if (window.opener) {
            window.close();
        } else {
            window.location.href = '/owner/school-website';
        }
    };

    return (
        <div className="h-screen bg-slate-100 flex flex-col pt-8 overflow-hidden">
            {/* Banner */}
            <div className="fixed top-0 left-0 w-full bg-yellow-400 text-yellow-900 text-center text-xs py-1 font-bold z-[100] shadow-md">
                PREVIEW MODE - Changes are not published unless applied
            </div>

            {/* Control Toolbar */}
            <div className="bg-slate-900 text-white p-4 shadow-lg flex flex-col md:flex-row justify-between items-center gap-4 sticky top-6 z-[99] mx-4 rounded-xl">
                <div className="flex items-center gap-4">
                     <div className="bg-[#228B22] p-2 rounded-lg">
                         <LayoutTemplate size={20} />
                     </div>
                     <div>
                         <h2 className="font-bold text-sm text-slate-400 uppercase tracking-wider">Template Preview</h2>
                         <div className="text-lg font-bold flex items-center gap-2">
                             {currentDesign.name}
                             <span className="text-xs bg-slate-700 px-2 py-0.5 rounded text-slate-300 font-normal">
                                 {selectedDesignIndex + 1} / {availableDesigns.length}
                             </span>
                         </div>
                         <div className="text-xs text-blue-400 font-mono mt-0.5">
                            Target: {previewData?.subdomain}.sonoma.school
                         </div>
                     </div>
                </div>

                {/* Viewport Toggles */}
                <div className="flex bg-slate-800 rounded-lg p-1 gap-1">
                    <button onClick={() => setViewMode('desktop')} title="Desktop preview" aria-label="Desktop preview" className={`p-2 rounded ${viewMode === 'desktop' ? 'bg-slate-600' : 'hover:bg-slate-700'}`}><Monitor size={18}/></button>
                    <button onClick={() => setViewMode('tablet')} title="Tablet preview" aria-label="Tablet preview" className={`p-2 rounded ${viewMode === 'tablet' ? 'bg-slate-600' : 'hover:bg-slate-700'}`}><Tablet size={18}/></button>
                    <button onClick={() => setViewMode('mobile')} title="Mobile preview" aria-label="Mobile preview" className={`p-2 rounded ${viewMode === 'mobile' ? 'bg-slate-600' : 'hover:bg-slate-700'}`}><Smartphone size={18}/></button>
                </div>

                <div className="flex-1 max-w-md mx-4 hidden md:flex items-center justify-center text-xs uppercase tracking-widest text-slate-400">
                    Design1 only enabled for now
                </div>

                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={handleClose} 
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    >
                        Close Preview
                    </Button>

                    <Button 
                        onClick={() => setIsEditing(!isEditing)} 
                        variant="secondary"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                        {isEditing ? <><LayoutTemplate className="mr-2 h-4 w-4" /> Preview Design</> : <><Edit className="mr-2 h-4 w-4" /> Edit Design</>}
                    </Button>

                    {isEditing && (
                        <Button 
                            onClick={() => setShowThemeEditor(!showThemeEditor)}
                            variant="secondary"
                            className={showThemeEditor ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "bg-slate-700 hover:bg-slate-600 text-white"}
                        >
                            <Palette className="mr-2 h-4 w-4" /> Theme
                        </Button>
                    )}

                    {isEditing && (
                        <Button 
                            onClick={handleSaveEdits}
                            disabled={saving}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                             {saving ? "Saving..." : <><Save className="mr-2 h-4 w-4" /> Save Edits</>}
                        </Button>
                    )}
                    
                    <Button 
                        onClick={handleApplyStyle} 
                        disabled={applying || isEditing}
                        className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
                    >
                        {applying ? "Publishing..." : <><Check className="mr-2 h-4 w-4" /> Publish Design</>}
                    </Button>
                    
                    {availableDesigns.length > 1 && (
                        <>
                            <div className="w-px h-8 bg-slate-700 mx-2"></div>

                            <Button variant="outline" onClick={prevDesign} disabled={isEditing} className="border-slate-600 hover:bg-slate-800 text-white">
                                <ChevronLeft className="mr-2 h-4 w-4" /> Prev
                            </Button>
                            <Button onClick={nextDesign} disabled={isEditing} className="bg-[#228B22] hover:bg-[#1a6b1a] text-white">
                                Next Template <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className={`flex-1 overflow-auto transition-all duration-300 mx-auto bg-white shadow-2xl relative z-10 transform-gpu ${
                viewMode === 'mobile' ? 'w-[375px] my-8 rounded-3xl border-[12px] border-slate-800 h-[750px] shrink-0' : 
                viewMode === 'tablet' ? 'w-[768px] my-8 rounded-xl border-[12px] border-slate-800 h-[1024px] shrink-0' : 
                'w-full h-full'
            }`}>
                 {/* Render Active Design inside an IFrame to ensure media queries work */}
                <IFramePreview>
                    <DesignComponent 
                        previewData={{
                            ...previewData,
                            theme,
                            sectionVisibility,
                            nav: navigation,
                            colors: theme.colors
                        }} 
                        isEditMode={isEditing}
                        onEditSection={(section) => console.log('Edit section:', section)}
                        onUpdateField={handleFieldUpdate}
                    />
                </IFramePreview>
            </div>
            
            {/* Theme Editor Panel */}
            {showThemeEditor && isEditing && (
                <div className="fixed top-24 right-4 z-[95] animate-in slide-in-from-right-10 duration-300">
                    <div className="relative">
                        <button 
                            onClick={() => setShowThemeEditor(false)}
                            title="Close theme editor"
                            aria-label="Close theme editor"
                            className="absolute -top-2 -right-2 bg-slate-700 hover:bg-red-500 text-white rounded-full p-1.5 z-10 transition-colors"
                        >
                            <X size={14} />
                        </button>
                        <ThemeEditor
                            theme={theme}
                            sectionVisibility={sectionVisibility}
                            navigation={navigation}
                            onThemeChange={(newTheme) => {
                                setTheme(newTheme);
                                // Also update previewData for persistence
                                setPreviewData((prev: any) => ({ ...prev, theme: newTheme, colors: newTheme.colors }));
                            }}
                            onSectionVisibilityChange={(newVisibility) => {
                                setSectionVisibility(newVisibility);
                                setPreviewData((prev: any) => ({ ...prev, sectionVisibility: newVisibility }));
                            }}
                            onNavigationChange={(newNav) => {
                                setNavigation(newNav);
                                setPreviewData((prev: any) => ({ ...prev, nav: newNav }));
                            }}
                            onUpdateField={handleFieldUpdate}
                        />
                    </div>
                </div>
            )}
            
            {/* Design Description Toast - Hide when editing */}
            {!isEditing && (
                <div className="fixed bottom-6 left-6 max-w-sm bg-slate-900/90 backdrop-blur text-white p-4 rounded-lg shadow-xl border border-slate-700 hidden md:block z-[90]">
                    <h4 className="font-bold text-[#D4AF37] mb-1">About this Design</h4>
                    <p className="text-sm text-slate-300 leading-relaxed">
                        {currentDesign.description}
                    </p>
                </div>
            )}
        </div>
    );
};

export default SchoolWebsitePreview;
